import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { LuUsers, LuWallet, LuChartBar, LuActivity, LuClock } from 'react-icons/lu';
import { Link } from 'react-router-dom'; // ❗️ Для ссылки на "всех пользователей"

// --- Компонент: Карточка статистики (Виджет) ---
// (Внес небольшие изменения в formatValue для .toFixed(2))
const StatCard = ({ title, value, icon, currency = '' }) => {
  const formatValue = (val) => {
    const numValue = parseFloat(val);
    if (isNaN(numValue)) return val;
    
    // Если это целое число (напр. 10 пользователей)
    if (numValue % 1 === 0) {
        if (numValue >= 1000) return `${(numValue / 1000).toFixed(1)}k`;
        return numValue;
    }
    // Если это деньги (напр. 100.50)
    return numValue.toFixed(2);
  };
  
  return (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' }}>
        <div className="stat-icon">
          {icon}
        </div>
        <div className="stat-content">
          <h3 className="stat-title">{title}</h3>
          <div className="stat-value">
            {currency}{formatValue(value)}
          </div>
        </div>
      </div>
    </div>
  );
};

// --- ❗️ НОВЫЙ КОМПОНЕНТ: Виджет "Последние пользователи" ---
const RecentUsersWidget = ({ users }) => {
    // Берем 5 последних пользователей
    const recentUsers = users.slice(0, 5);

    return (
        <div className="widget-card">
            <h3 className="widget-title">Последние регистрации</h3>
            <div className="widget-content">
                {recentUsers.length === 0 && <div className="loading-mini">Нет пользователей...</div>}
                <ul className="user-widget-list">
                    {recentUsers.map(user => (
                        <li key={user.id} className="user-widget-item">
                            <span className="user-widget-email">{user.email}</span>
                            <span className={`role-badge role-${user.role}`}>{user.role}</span>
                        </li>
                    ))}
                </ul>
                <Link to="/admin/users" className="widget-footer-link">
                    Показать всех &rarr;
                </Link>
            </div>
        </div>
    );
};

// --- ❗️ НОВЫЙ КОМПОНЕНТ: Виджет "Статистика продаж" ---
const SalesStatisticsWidget = ({ sales, loading }) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('ru-RU', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatAmount = (amount, currency) => {
        const currencySymbols = {
            'USD': '$',
            'UAH': '₴',
            'RUB': '₽'
        };
        const symbol = currencySymbols[currency] || currency;
        return `${symbol}${parseFloat(amount).toFixed(2)}`;
    };

    const getProviderName = (provider) => {
        const names = {
            'crystalpay': 'CrystalPay',
            'heleket': 'Heleket',
            'yookassa': 'YooKassa',
            'platega': 'Platega',
            'mulenpay': 'Mulenpay',
            'urlpay': 'UrlPay',
            'telegram_stars': 'Telegram Stars',
            'balance': 'Баланс'
        };
        return names[provider] || provider || 'N/A';
    };

    return (
        <div className="widget-card" style={{ width: '100%', marginTop: '20px' }}>
            <h3 className="widget-title">Статистика продаж</h3>
            <div className="widget-content">
                {loading && <div className="loading-mini">Загрузка...</div>}
                {!loading && sales.length === 0 && (
                    <div className="loading-mini">Продажи не найдены</div>
                )}
                {!loading && sales.length > 0 && (
                    <>
                        <div className="sales-table-wrapper">
                            <table className="sales-table">
                                <thead>
                                    <tr>
                                        <th>Дата</th>
                                        <th>Пользователь</th>
                                        <th>Тариф</th>
                                        <th>Сумма</th>
                                        <th>Провайдер</th>
                                        <th>Промокод</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.map((sale) => (
                                        <tr key={sale.id}>
                                            <td>{formatDate(sale.date)}</td>
                                            <td>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span style={{ fontWeight: 500 }}>
                                                        {sale.user?.email || 'N/A'}
                                                    </span>
                                                    {sale.user?.telegram_username && (
                                                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                            @{sale.user.telegram_username}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {sale.is_balance_topup ? (
                                                    <span style={{ 
                                                        color: '#10b981', 
                                                        fontWeight: 500,
                                                        fontSize: '13px'
                                                    }}>
                                                        💰 Пополнение баланса
                                                    </span>
                                                ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                    <span style={{ fontWeight: 500 }}>
                                                        {sale.tariff?.name || 'N/A'}
                                                    </span>
                                                    {sale.tariff?.duration_days && (
                                                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                                            {sale.tariff.duration_days} дн.
                                                        </span>
                                                    )}
                                                </div>
                                                )}
                                            </td>
                                            <td style={{ fontWeight: 600 }}>
                                                {formatAmount(sale.amount, sale.currency)}
                                            </td>
                                            <td>
                                                <span className="provider-badge">
                                                    {getProviderName(sale.payment_provider)}
                                                </span>
                                            </td>
                                            <td>
                                                {sale.promo_code ? (
                                                    <span className="promo-badge">{sale.promo_code}</span>
                                                ) : (
                                                    <span style={{ color: 'var(--text-secondary)' }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Link to="/admin/sales" className="widget-footer-link">
                            Показать все отчеты по продажам &rarr;
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};

// --- V3: Улучшенный виджет "Ноды" с подробной информацией ---
const NodesWidget = ({ nodes, loading }) => {
    const getNodeStatus = (node) => {
        if (node.isDisabled) return 'disabled';
        if (node.isConnecting) return 'connecting';
        if (node.isConnected) return 'online';
        return 'offline';
    };

    const getStatusInfo = (status) => {
        const statuses = {
            online: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', label: 'Online' },
            offline: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.15)', label: 'Offline' },
            disabled: { color: '#6b7280', bg: 'rgba(107, 114, 128, 0.15)', label: 'Disabled' },
            connecting: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', label: 'Connecting' },
        };
        return statuses[status] || statuses.offline;
    };

    const formatBytes = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
    };

    const formatUptime = (seconds) => {
        if (!seconds) return 'N/A';
        const sec = parseInt(seconds, 10);
        const days = Math.floor(sec / 86400);
        const hours = Math.floor((sec % 86400) / 3600);
        if (days > 0) return `${days}д ${hours}ч`;
        if (hours > 0) return `${hours}ч`;
        return `${Math.floor((sec % 3600) / 60)}м`;
    };

    const getCountryFlag = (code) => {
        if (!code) return '🌐';
        const flags = {
            FR: '🇫🇷', DE: '🇩🇪', NL: '🇳🇱', US: '🇺🇸', GB: '🇬🇧',
            UA: '🇺🇦', PL: '🇵🇱', FI: '🇫🇮', SE: '🇸🇪', NO: '🇳🇴',
            RU: '🇷🇺', CA: '🇨🇦', JP: '🇯🇵', SG: '🇸🇬', AU: '🇦🇺',
            TR: '🇹🇷', IT: '🇮🇹', ES: '🇪🇸',
        };
        return flags[String(code).toUpperCase()] || '🌐';
    };

    const stats = {
        total: nodes.length,
        online: nodes.filter((n) => n.isConnected && !n.isDisabled).length,
        totalUsers: nodes.reduce((sum, n) => sum + (n.usersOnline || 0), 0),
        totalTraffic: nodes.reduce((sum, n) => sum + (n.trafficUsedBytes || 0), 0),
    };

    return (
        <div className="widget-card">
            <h3 className="widget-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>Ноды</span>
                <span
                    style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        background: stats.online === stats.total ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.15)',
                        color: stats.online === stats.total ? '#10b981' : '#f59e0b',
                    }}
                >
                    {stats.online}/{stats.total} online
                </span>
            </h3>
            <div className="widget-content">
                {loading && <div className="loading-mini">Загрузка...</div>}
                {!loading && nodes.length === 0 && <div className="loading-mini">Ноды не найдены</div>}
                {!loading && nodes.length > 0 && (
                    <>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(3, 1fr)',
                                gap: '8px',
                                marginBottom: '16px',
                                padding: '12px',
                                background: 'var(--surface-muted)',
                                borderRadius: '10px',
                            }}
                        >
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>{stats.totalUsers}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Онлайн</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>{formatBytes(stats.totalTraffic)}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Трафик</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)' }}>{stats.total}</div>
                                <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>Нод</div>
                            </div>
                        </div>

                        <div className="nodes-widget-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {nodes.slice(0, 6).map((node, index) => {
                                const status = getNodeStatus(node);
                                const statusInfo = getStatusInfo(status);
                                return (
                                    <div
                                        key={node.uuid || index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            padding: '10px 12px',
                                            background: 'var(--surface-muted)',
                                            borderRadius: '10px',
                                            borderLeft: `3px solid ${statusInfo.color}`,
                                        }}
                                    >
                                        <div style={{ fontSize: '20px' }}>{getCountryFlag(node.countryCode)}</div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div
                                                style={{
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    color: 'var(--text-primary)',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {node.name || 'Unnamed'}
                                            </div>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '10px',
                                                    fontSize: '11px',
                                                    color: 'var(--text-secondary)',
                                                    marginTop: '3px',
                                                }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                    <LuUsers size={11} /> {node.usersOnline ?? 0}
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                    <LuActivity size={11} /> {formatBytes(node.trafficUsedBytes)}
                                                </span>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                    <LuClock size={11} /> {formatUptime(node.xrayUptime)}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                background: statusInfo.bg,
                                                color: statusInfo.color,
                                                fontSize: '10px',
                                                fontWeight: '600',
                                            }}
                                        >
                                            {statusInfo.label}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <Link to="/admin/nodes" className="widget-footer-link">
                            Управление нодами &rarr;
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
};


// --- Основная страница Дашборда ---
function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [nodesLoading, setNodesLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Запускаем запросы параллельно
        const [statsResponse, usersResponse] = await Promise.all([
          fetch('/api/admin/statistics', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/admin/users', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (!statsResponse.ok) throw new Error('Не удалось загрузить статистику');
        if (!usersResponse.ok) throw new Error('Не удалось загрузить пользователей');

        const statsData = await statsResponse.json();
        const usersData = await usersResponse.json();
        
        setStats(statsData);
        setUsers(usersData.sort((a, b) => b.id - a.id)); 
        
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchAllData();
    }
  }, [token]);

  // Загрузка нод отдельно
  useEffect(() => {
    const fetchNodes = async () => {
      setNodesLoading(true);
      try {
        const response = await fetch('/api/admin/nodes', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const nodesList = Array.isArray(data) ? data : [];
          setNodes(nodesList);
        }
      } catch (e) {
        console.error('Ошибка загрузки нод:', e);
      } finally {
        setNodesLoading(false);
      }
    };
    
    if (token) {
      fetchNodes();
    }
  }, [token]);

  // Загрузка продаж отдельно (только 5 последних для дашборда)
  useEffect(() => {
    const fetchSales = async () => {
      setSalesLoading(true);
      try {
        const response = await fetch('/api/admin/sales?limit=5', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const salesList = Array.isArray(data) ? data : [];
          setSales(salesList);
        }
      } catch (e) {
        console.error('Ошибка загрузки продаж:', e);
      } finally {
        setSalesLoading(false);
      }
    };
    
    if (token) {
      fetchSales();
    }
  }, [token]);

  if (loading) return <main className="admin-page-content"><div className="loading-mini">Загрузка дашборда...</div></main>;
  if (error) return <main className="admin-page-content"><div className="message-error">{error}</div></main>;
  if (!stats) return <main className="admin-page-content"><div className="loading-mini">Нет данных.</div></main>;

  // (Логика подсчета та же)
  const totalUAH = stats.total_revenue?.UAH || 0;
  const totalRUB = stats.total_revenue?.RUB || 0;
  const totalUSD = stats.total_revenue?.USD || 0;
  const todayUAH = stats.today_revenue?.UAH || 0;
  const todayRUB = stats.today_revenue?.RUB || 0;
  const todayUSD = stats.today_revenue?.USD || 0;

  return (
    <main className="admin-page-content">
      {/* ❗️❗️❗️ НОВЫЙ МАКЕТ С КОЛОНКАМИ ❗️❗️❗️ */}
      <div className="dashboard-grid-layout">
        
        {/* --- 1. Основная колонка (Статистика) --- */}
        <div className="main-column">
          <section>
            <h2>Дашборд</h2>
            
            <h3>Общая статистика</h3>
            <div className="stats-grid">
              <StatCard title="Всего пользователей" value={stats.total_users || 0} icon={<LuUsers />} />
              <StatCard title="Всего продаж" value={stats.total_sales_count || 0} icon={<LuChartBar />} />
              <StatCard title="Прибыль (USD)" value={totalUSD} currency="$" icon={<LuWallet />} />
              <StatCard title="Прибыль (UAH)" value={totalUAH} currency="₴" icon={<LuWallet />} />
              <StatCard title="Прибыль (RUB)" value={totalRUB} currency="₽" icon={<LuWallet />} />
            </div>
            
            <h3 style={{marginTop: '30px'}}>За сегодня</h3>
            <div className="stats-grid stats-grid-today">
               <StatCard title="Прибыль сегодня (USD)" value={todayUSD} currency="$" icon={<LuActivity />} />
               <StatCard title="Прибыль сегодня (UAH)" value={todayUAH} currency="₴" icon={<LuActivity />} />
               <StatCard title="Прибыль сегодня (RUB)" value={todayRUB} currency="₽" icon={<LuActivity />} />
            </div>
          </section>

          {/* Виджет статистики продаж */}
          <SalesStatisticsWidget sales={sales} loading={salesLoading} />
        </div>
        
        {/* --- 2. Боковая колонка (Виджеты) --- */}
        <div className="sidebar-column">
            <RecentUsersWidget users={users} />
            <NodesWidget nodes={nodes} loading={nodesLoading} />
        </div>
        
      </div>
    </main>
  );
}

export default AdminDashboardPage;